import { motion } from "motion/react";
import { Quote } from "lucide-react";

export function About() {
  return (
    <section id="about" className="py-24 overflow-hidden bg-background">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left Column: Image & Experience */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="sticky top-24"
          >
            <div className="relative">
              <div className="pill-image h-[260px] md:h-[360px] w-full max-w-[220px] mx-auto overflow-hidden shadow-2xl border-8 border-white">
                <img 
                  src="/Johan Head shot.jpg" 
                  alt="Johan Oosthuizen - Professional Coach" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Floating Location Badge */}
              <motion.div 
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.5 }}
                viewport={{ once: true }}
                className="absolute -bottom-6 -right-6 md:right-12 bg-primary p-6 rounded-3xl shadow-xl text-white max-w-[220px]"
              >
                <p className="font-serif italic text-xl mb-1">Pretoria, SA</p>
                <p className="text-xs uppercase tracking-widest opacity-80">Based in South Africa, serving the world.</p>
              </motion.div>
            </div>

            {/* Quote Block 1 */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              className="mt-16 p-8 bg-secondary/30 rounded-[2.5rem] border border-primary/5 italic text-lg text-primary-foreground/80 leading-relaxed relative"
            >
              <Quote className="absolute -top-4 -left-4 w-10 h-10 text-primary/20" />
              "If there is any difference between you and me, it may simply be that I get up every day and have a chance to do what I love to do, every day."
              <footer className="mt-4 text-sm font-sans font-semibold not-italic text-primary">— Donald O. Clifton</footer>
            </motion.div>
          </motion.div>

          {/* Right Column: Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-12"
          >
            <div>
              <span className="text-primary font-semibold tracking-widest uppercase text-sm mb-4 block">Our Story & Philosophy</span>
              <h2 className="text-4xl md:text-6xl font-serif mb-8 leading-tight">
                Strengthsfinder <span className="italic">Coaching</span>
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Based in Pretoria, South-Africa, Iwantcoaching is a company that focus’ only on Strengthsfinder coaching as well as career guidance. We are accredited Strengthsfinder individual, group and full 34 specialists.
                </p>
                <p>
                  We are dedicated to give each individual and company great service as well as great quality of work. We rise to each occasion and only settle to give you the absolute best coaching and career guidance possible.
                </p>
                <p className="font-medium text-foreground">
                  We are also focussed on building relationships and making sure that each individual need and company need is met! We want to make a difference in each person and companies life!
                </p>
              </div>
            </div>

            {/* Strength Focus Section */}
            <div className="space-y-6">
              <h3 className="text-2xl font-serif text-primary italic">We are a strength focussed company</h3>
              <div className="p-8 bg-muted rounded-[2rem] border-l-4 border-primary space-y-4">
                <p className="text-lg italic leading-relaxed">
                  “You will excel only by maximizing your strengths, never by fixing your weaknesses. This is not the same as saying 'ignore your weaknesses."
                </p>
                <p className="text-sm text-muted-foreground">
                  Our definition of a weakness is anything that gets in the way of excellent performance.
                </p>
                <footer className="text-xs font-semibold uppercase tracking-wider text-primary">— Donald O. Clifton</footer>
              </div>
            </div>

            {/* Live by Example Section */}
            <div className="space-y-6">
              <h3 className="text-2xl font-serif text-primary italic">We live by example</h3>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  All our coaches have received the coaching as well as career guidance. The strength of the team is each individual member and the strength of each member is the team. 
                </p>
                <p>
                  Our qualified and accredited Strengthsfinder/Explorer and Career guidance coaches are all individuals who daily push themselves to be better at what they do and who they are—and because they do it themselves, they’ll be able to help you as an individual or team to also do it!
                </p>
              </div>
            </div>

            {/* Inspiration Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-secondary/20 rounded-3xl">
                <p className="italic text-sm mb-3">"Everyone has special talents, and it is our duty to find ours and use them well."</p>
                <p className="text-xs font-bold uppercase tracking-tighter">— John Templeton</p>
              </div>
              <div className="p-6 bg-secondary/20 rounded-3xl">
                <p className="italic text-sm mb-3">"The whole point of being alive is to evolve into the complete person you were intended to be."</p>
                <p className="text-xs font-bold uppercase tracking-tighter">— Oprah</p>
              </div>
            </div>

            {/* Jackie Chan Quote */}
            <div className="text-center py-8 border-y border-primary/10">
              <p className="text-xl font-serif italic mb-2">"I never wanted to be the next Bruce Lee. I just wanted to be the first Jackie Chan."</p>
              <p className="text-sm font-sans font-semibold text-primary">— Jackie Chan</p>
            </div>

            {/* Final CTA */}
            <div className="pt-8">
              <h4 className="text-3xl md:text-4xl font-serif text-center text-primary">
                Discover <span className="italic">The Real You!</span>
              </h4>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
